import { useState, useMemo, useCallback } from 'react'
import { Activity, Clock, CheckCircle2, XCircle, Layers, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react'
import { useJobQueueSummary } from '@/hooks/useJobs'
import { useTenants } from '@/hooks/useTenants'
import { useProvisionLogsByJob } from '@/hooks/useProvisionLogs'
import { formatRelativeTime, formatDate, jobStatusVariant } from '@/lib/utils'
import { Badge, Skeleton, EmptyState } from '@/components/ui'
import { ProvisionLogViewer } from '@/components/logs/ProvisionLogViewer'
import { MilestoneProgress } from '@/components/logs/MilestoneProgress'
import type { TJob } from '@/types'
import { PROVISION_MILESTONES, TEARDOWN_MILESTONES, REDEPLOY_MILESTONES, UPDATE_MILESTONES } from '@/types'

// ── Duration formatting ────────────────────────────────────────────────────

function pbDate(d: string | undefined | null): Date {
  if (!d) return new Date(0)
  return new Date(d.replace(' ', 'T'))
}

function formatJobDuration(startedAt: string | null | undefined, completedAt: string | null | undefined, status: string): string {
  if (!startedAt) return '\u2014'
  const start = pbDate(startedAt)
  if (isNaN(start.getTime())) return '\u2014'
  const end = status === 'running' ? new Date() : completedAt ? pbDate(completedAt) : null
  if (!end || isNaN(end.getTime())) return '\u2014'
  const totalSec = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000))
  const hours = Math.floor(totalSec / 3600)
  const min = Math.floor((totalSec % 3600) / 60)
  const sec = totalSec % 60
  if (hours > 0) return `${hours}h ${min.toString().padStart(2, '0')}m`
  if (min > 0) return `${min}m ${sec.toString().padStart(2, '0')}s`
  return `${sec}s`
}

// ── Stat Pill ──────────────────────────────────────────────────────────────

function StatPill({ icon, label, value, color, pulse }: {
  icon: React.ReactNode; label: string; value: number; color: string; pulse?: boolean
}) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 16px',
      backgroundColor: 'var(--bg-secondary)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-full)',
      fontSize: 13,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-tertiary)' }}>{icon}</span>
      <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {pulse && value > 0 && (
          <span className="animate-pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
        )}
        <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      </span>
    </div>
  )
}

// ── Expanded Detail ────────────────────────────────────────────────────────

function MetadataItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {value || '\u2014'}
      </span>
    </div>
  )
}

function ExpandedDetail({ job, tenantName }: { job: TJob; tenantName: string }) {
  const { data: provisionLogs = [], provisionId, provision } = useProvisionLogsByJob(job.id)

  const milestoneDefs = (() => {
    switch (job.job_type) {
      case 'provisioning': return PROVISION_MILESTONES
      case 'teardown': return TEARDOWN_MILESTONES
      case 'redeploy': return REDEPLOY_MILESTONES
      case 'update': return UPDATE_MILESTONES
      default: return PROVISION_MILESTONES
    }
  })()

  const codeBlockStyle: React.CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    lineHeight: 1.6,
    backgroundColor: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    padding: 16,
    maxHeight: 200,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  }

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 8,
  }

  const hasInput = job.input_data && Object.keys(job.input_data).length > 0
  const hasError = job.status === 'failed' && job.error_log
  const hasProvision = !!provisionId

  return (
    <div className="fade-slide-up" style={{
      padding: '16px 16px 16px 48px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      borderTop: '1px solid var(--border-subtle)',
      backgroundColor: 'var(--bg-primary)',
    }}>
      {/* Metadata grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: '12px 24px',
        padding: '12px 16px',
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
      }}>
        <MetadataItem label="Job Type" value={
          <Badge variant={jobStatusVariant(job.status)}>{job.job_type}</Badge>
        } />
        <MetadataItem label="Status" value={
          <Badge variant={jobStatusVariant(job.status)} dot={job.status === 'running'} pulse={job.status === 'running'}>{job.status}</Badge>
        } />
        <MetadataItem label="Tenant" value={tenantName} />
        <MetadataItem label="Created" value={formatDate(job.created_at || job.created)} />
        <MetadataItem label="Started" value={job.started_at ? formatDate(job.started_at) : '\u2014'} />
        <MetadataItem label="Completed" value={job.completed_at ? formatDate(job.completed_at) : '\u2014'} />
        <MetadataItem label="Duration" value={
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            {formatJobDuration(job.started_at, job.completed_at, job.status)}
          </span>
        } />
      </div>

      {/* Provision milestone + logs */}
      {hasProvision && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={sectionLabelStyle}>Provision Progress</div>
          <MilestoneProgress
            logs={provisionLogs}
            milestoneDefs={milestoneDefs}
            startedAt={provision?.started_at ?? job.started_at}
            completedAt={provision?.completed_at ?? job.completed_at}
          />
          <div style={{ ...sectionLabelStyle, marginTop: 4 }}>Provision Logs</div>
          <ProvisionLogViewer provisionId={provisionId} maxHeight="280px" />
        </div>
      )}

      {/* Input data */}
      {hasInput && (
        <div>
          <div style={sectionLabelStyle}>Input Data</div>
          <div style={codeBlockStyle}>
            <code style={{ color: 'var(--text-secondary)' }}>
              {JSON.stringify(job.input_data, null, 2)}
            </code>
          </div>
        </div>
      )}

      {/* Error log */}
      {hasError && (
        <div>
          <div style={{ ...sectionLabelStyle, color: 'var(--status-unhealthy)' }}>Error Log</div>
          <div style={codeBlockStyle}>
            <code style={{ color: 'var(--status-unhealthy)' }}>
              {job.error_log}
            </code>
          </div>
        </div>
      )}

      {/* Empty state if nothing to show */}
      {!hasInput && !hasError && !hasProvision && (
        <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
          No additional details available.
        </div>
      )}
    </div>
  )
}

// ── Skeleton Row ───────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1.2fr 1fr 1fr 0.8fr',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid var(--border-subtle)',
      gap: 12,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Skeleton width={160} height={14} />
        <Skeleton width={100} height={10} />
      </div>
      <Skeleton width={90} height={14} />
      <Skeleton width={70} height={22} borderRadius="var(--radius-full)" />
      <Skeleton width={70} height={14} />
      <Skeleton width={50} height={14} />
    </div>
  )
}

// ── Job Row ────────────────────────────────────────────────────────────────

function JobRow({ job, tenantName, isExpanded, onToggle }: {
  job: TJob; tenantName: string; isExpanded: boolean; onToggle: () => void
}) {
  return (
    <div style={{ borderBottom: '1px solid var(--border-subtle)' }}>
      <div
        className="table-row"
        onClick={onToggle}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1.2fr 1fr 1fr 0.8fr',
          alignItems: 'center',
          padding: '12px 16px',
          cursor: 'pointer',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ color: 'var(--text-tertiary)', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {job.title}
            </div>
            {job.description && (
              <div style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {job.description}
              </div>
            )}
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tenantName}
        </div>

        <div>
          <Badge variant={jobStatusVariant(job.status)} dot={job.status === 'running'} pulse={job.status === 'running'}>
            {job.status}
          </Badge>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
          {formatRelativeTime(job.created_at || job.created)}
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
          {formatJobDuration(job.started_at, job.completed_at, job.status)}
        </div>
      </div>

      {isExpanded && <ExpandedDetail job={job} tenantName={tenantName} />}
    </div>
  )
}

// ── Jobs Page ──────────────────────────────────────────────────────────────

export default function JobsPage() {
  const { summary, jobs, isLoading, isError } = useJobQueueSummary()
  const { data: tenants = [] } = useTenants()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const tenantMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const t of tenants) map[t.id] = t.name
    return map
  }, [tenants])

  const toggleExpand = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id)
  }, [])

  const sortedJobs = useMemo(() => {
    return [...jobs].sort((a, b) => {
      return pbDate(b.created_at || b.created).getTime() - pbDate(a.created_at || a.created).getTime()
    })
  }, [jobs])

  const columnHeaders = ['Type', 'Tenant', 'Status', 'Created', 'Duration']

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>

        {isError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 16px', marginBottom: 16,
            backgroundColor: 'var(--status-unhealthy-dim)',
            border: '1px solid var(--status-unhealthy)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13, color: 'var(--status-unhealthy)',
          }}>
            <AlertTriangle size={16} />
            Failed to load jobs.
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
          <StatPill icon={<Layers size={14} />} label="Total" value={isLoading ? 0 : summary.total} color="var(--text-primary)" />
          <StatPill icon={<Clock size={14} />} label="Queued" value={isLoading ? 0 : summary.queued} color="var(--job-queued)" />
          <StatPill icon={<Activity size={14} />} label="Running" value={isLoading ? 0 : summary.running} color="var(--job-running)" pulse />
          <StatPill icon={<CheckCircle2 size={14} />} label="Complete" value={isLoading ? 0 : summary.complete} color="var(--job-complete)" />
          <StatPill icon={<XCircle size={14} />} label="Failed" value={isLoading ? 0 : summary.failed} color="var(--job-failed)" />
        </div>

        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.2fr 1fr 1fr 0.8fr',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: 'var(--bg-tertiary)',
            borderBottom: '1px solid var(--border-subtle)',
            gap: 12,
          }}>
            {columnHeaders.map(header => (
              <div key={header} style={{
                fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)',
                textTransform: 'uppercase', letterSpacing: '0.05em', userSelect: 'none',
              }}>
                {header}
              </div>
            ))}
          </div>

          {isLoading ? (
            <>{Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}</>
          ) : sortedJobs.length === 0 ? (
            <EmptyState
              icon={<Activity size={32} />}
              title="No jobs in queue"
              description="Jobs will appear here when provisioning or health checks run."
            />
          ) : (
            sortedJobs.map(job => (
              <JobRow
                key={job.id}
                job={job}
                tenantName={job.tenant ? (tenantMap[job.tenant] || '\u2014') : '\u2014'}
                isExpanded={expandedId === job.id}
                onToggle={() => toggleExpand(job.id)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}
